import { useMemo, useState } from "react";
import { ConnectProto } from "@racoon/api";
import { startOfLocalDay, toTimestamp } from "@racoon/lib";
import { skipToken } from "@reduxjs/toolkit/query";
import { useListJobsQuery } from "~/shared/api/jobs/job.grpc";
import { useListContainerGroupsQuery } from "~/shared/api/container-group/container-group.grpc";
import { dayRange } from "~/shared/lib/pickup-status/use-pickup-status";
import { dueJobTypesByCgId, jobsOwnedByGroup } from "./helpers";
import { useContainerGroupSchedules } from "~/shared/api/container-group-schedule/use-location-schedules";

const S = ConnectProto.JobStatus;
// Every status but cancelled, as the generator's own "job exists for the day"
// check: cancelling hands the day back to the generator, nothing else does.
const LIVE_STATUSES = Object.values(S).filter(
  (status): status is ConnectProto.JobStatus =>
    typeof status === "number" && status !== S.UNDEFINED && status !== S.CANCELLED,
);

// A group is sized for one truck's day, so the day's jobs at its sites stay far
// below the API's page cap; a second page is not fetched.
const JOBS_PAGE_SIZE = 100;

type Args = {
  group?: ConnectProto.PickupLocationGroup;
  /** The company's groups with their links: they decide which unstamped jobs
   *  are this group's. */
  groups: ConnectProto.PickupLocationGroup[];
  serviceProviderId?: bigint;
};

export type ServiceDayFilter = {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  date: Date;
  setDate: (date: Date) => void;
  /** The first day the view may show: today. Past days are not offered. */
  minDate: Date;
  /** Job types due per container group id on `date`; null while the filter is off. */
  dueJobTypesByCgId: Map<string, string[]> | null;
  /** The day's jobs, schedules or zones are still being fetched, so the rows may still grow. */
  loading: boolean;
  /** The schedules or the zones could not be loaded: the filtered day shows nothing rather than every row. */
  failed: boolean;
};

/**
 * The single-day view of a group: which of its container groups are due on
 * one service day, by schedule or by an already planned job. Owned by the page
 * so the table and the map narrow to the same rows.
 */
export const useServiceDayFilter = ({
  group,
  groups,
  serviceProviderId,
}: Args): ServiceDayFilter => {
  const [enabled, setEnabled] = useState(false);
  const today = startOfLocalDay(new Date());
  const [pickedDate, setDate] = useState(today);
  // A day picked before midnight is in the past after it: the view starts at today.
  const date = pickedDate < today ? today : pickedDate;

  const dayKey = startOfLocalDay(date).getTime();
  // By the group's sites, not by its stamp: an unstamped job (an order placed
  // without a route group) still belongs to the group when its link is
  // exclusive, and jobsOwnedByGroup sorts them out below.
  const locationIds = useMemo(
    () => [...new Set((group?.containerGroups ?? []).map((cg) => cg.pickupLocationId))],
    [group],
  );
  const jobsArgs = useMemo(() => {
    // An empty id list would mean "every site", not "none".
    if (!enabled || locationIds.length === 0 || serviceProviderId == null) return skipToken;
    const { from, last } = dayRange(dayKey);
    return {
      serviceProviderIds: [serviceProviderId],
      pickupLocationIds: locationIds,
      jobStatuses: LIVE_STATUSES,
      timeFilter: { field: "tw_start_at", from: toTimestamp(from), to: toTimestamp(last) },
      listFilter: { pageSize: JOBS_PAGE_SIZE },
    };
  }, [enabled, locationIds, serviceProviderId, dayKey]);

  // `currentData`: while another day's request is in flight, `data` still holds
  // the previous day's jobs, which would keep yesterday's rows on screen.
  const { currentData: jobsData, isFetching } = useListJobsQuery(jobsArgs);

  const {
    schedulesByCgId,
    loaded: schedulesLoaded,
    failed: schedulesFailed,
    isFetching: isFetchingSchedules,
  } = useContainerGroupSchedules(group?.containerGroups);
  // The group response carries no zones; the container group list (unpaged without list_filter)
  // does.
  const {
    currentData: zonedData,
    isFetching: isFetchingZones,
    isError: zonesFailed,
  } = useListContainerGroupsQuery(
    enabled && group?.id ? { pickupLocationGroupIds: [group.id] } : skipToken,
  );
  const zoneByCgId = useMemo(
    () =>
      new Map(
        (zonedData?.containerGroups ?? []).map((cg) => [
          String(cg.id),
          cg.serviceProviderIds.map(String),
        ]),
      ),
    [zonedData],
  );

  const zonesPending = !zonedData && !zonesFailed;
  const due = useMemo(() => {
    if (!enabled || !group) return null;
    // Fails closed: while the schedules or the zones load, or when either failed, nothing is due, so
    // neither the table nor "create route" falls back to every link or to another depot's rows.
    if (schedulesFailed || zonesFailed || !schedulesLoaded || zonesPending) {
      return new Map<string, string[]>();
    }
    return dueJobTypesByCgId({
      group,
      schedulesByCgId,
      zoneByCgId,
      jobs: jobsOwnedByGroup({ group, groups, jobs: jobsData?.jobs ?? [] }),
      date: new Date(dayKey),
    });
  }, [
    enabled,
    group,
    groups,
    schedulesByCgId,
    zoneByCgId,
    schedulesLoaded,
    schedulesFailed,
    zonesFailed,
    zonesPending,
    jobsData,
    dayKey,
  ]);

  return {
    enabled,
    setEnabled,
    date,
    setDate,
    minDate: today,
    dueJobTypesByCgId: due,
    loading:
      enabled &&
      (isFetching || isFetchingSchedules || isFetchingZones || !schedulesLoaded || zonesPending),
    failed: enabled && (schedulesFailed || zonesFailed),
  };
};
